import { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { api, errorText, number } from './adminFormat';

// News can be refused by the reader; official messages (account, plan, security, terms) cannot.
export const CATEGORIES = {
  news: { label: 'News & blog', hint: 'Articles, announcements, offers. Every email has an unsubscribe link, and people who unsubscribed never receive it, even if selected.' },
  official: { label: 'Official', hint: 'Only information about their account, plan, security or the terms. Sent even to people who unsubscribed from news, so never for promotion.' },
};

const LEFT_OUT = {
  no_email: (n) => `${number(n)} without an email address (WhatsApp accounts)`,
  unsubscribed: (n) => `${number(n)} unsubscribed from news`,
  suspended: (n) => `${number(n)} suspended`,
  not_test_recipient: (n) => `${number(n)} not on this server's test list`,
};

export function leftOut(skipped = {}) {
  return Object.entries(LEFT_OUT).filter(([key]) => skipped[key] > 0).map(([key, text]) => text(skipped[key]));
}

// One email to many people: who receives it (and who is left out) is shown before sending; the
// server then sends in the background and this follows its progress.
export default function AdminBulkEmail({ audience, sending, onClose }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [category, setCategory] = useState('news');
  const [officialOk, setOfficialOk] = useState(false);
  const [state, setState] = useState({});
  const [campaign, setCampaign] = useState(null);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {   // who receives it depends on the kind of email
    let active = true;
    setPreview(null);
    api.post('/campaigns/preview', { audience, category })
      .then((data) => { if (active) setPreview(data); })
      .catch((err) => { if (active) setError(errorText(err)); });
    return () => { active = false; };
  }, [audience, category]);
  useEffect(() => {   // follow the sending until it is done or stops
    if (!campaign || campaign.state !== 'sending') return undefined;
    const timer = setTimeout(() => api.get(`/campaigns/${campaign.id}`).then(setCampaign).catch(() => {}), 2000);
    return () => clearTimeout(timer);
  }, [campaign]);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const off = sending?.mode === 'off';
  const recipients = preview?.recipients || 0;
  const ready = subject.trim() && body.trim() && confirmed && recipients > 0 && !off
    && (category === 'news' || officialOk);
  const choose = (value) => { setCategory(value); setConfirmed(false); setOfficialOk(false); setState({}); };
  const send = async (event) => {
    event.preventDefault();
    if (!ready || state.busy) return;
    setState({ busy: true });
    try {
      setCampaign(await api.post('/campaigns', { audience, category, subject: subject.trim(), body: body.trim() }));
      setState({});
    } catch (err) { setState({ message: errorText(err), error: true }); }
  };
  const done = campaign && campaign.sent + campaign.failed;

  return <div className="adm-panel-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="adm-panel" role="dialog" aria-modal="true" aria-labelledby="adm-bulk-title">
      <header className="adm-panel-header">
        <div><h2 id="adm-bulk-title">Group email</h2><p>From UpyaChat &lt;info@upyachat.com&gt;. Replies come back to info@upyachat.com.</p></div>
        <button ref={closeRef} type="button" className="ws-icon-button" onClick={onClose} aria-label="Close"><FiX /></button>
      </header>
      {campaign ? <section className="adm-progress" aria-live="polite">
        <h3>{campaign.subject}</h3>
        <div className="adm-progress-bar" aria-hidden="true"><span style={{ width: `${(done / Math.max(campaign.total, 1)) * 100}%` }} /></div>
        <p>{campaign.state === 'done' ? 'Finished: ' : campaign.state === 'sending' ? 'Sending: ' : 'Stopped: '}
          {number(campaign.sent)} sent{campaign.failed ? `, ${number(campaign.failed)} not delivered` : ''} of {number(campaign.total)}.</p>
        {campaign.state === 'sending' && <p className="adm-hint">You can close this window: sending carries on, and its progress is in the Group emails tab.</p>}
        {campaign.state === 'interrupted' && <p className="adm-hint">The sending stopped (server restart?). Resume it from the Group emails tab: nobody receives it twice.</p>}
        <button type="button" className="ws-secondary" onClick={onClose}>Close</button>
      </section> : error ? <p className="adm-feedback adm-error" role="alert">{error}</p> : !preview ? <p className="adm-muted" role="status">Counting the recipients…</p> : <form className="adm-form" onSubmit={send}>
        {off && <p className="adm-notice adm-notice-inline" role="note">Sending is turned off on this server.</p>}
        {sending?.mode === 'test' && <p className="adm-notice adm-notice-inline" role="note">Test mode: only this server's test recipients receive it.</p>}
        <fieldset className="adm-kinds">
          <legend>Kind of email</legend>
          {Object.entries(CATEGORIES).map(([value, kind]) => <label key={value} className={`adm-kind${category === value ? ' is-chosen' : ''}`}>
            <input type="radio" name="kind" value={value} checked={category === value} onChange={() => choose(value)} />
            <span><strong>{kind.label}</strong><small>{kind.hint}</small></span>
          </label>)}
        </fieldset>
        <div className="adm-audience">
          <strong>{number(recipients)} {recipients === 1 ? 'person' : 'people'} will receive it</strong>
          {preview.selected !== recipients && <span> out of {number(preview.selected)} selected</span>}
          {leftOut(preview.skipped).length > 0 && <ul>{leftOut(preview.skipped).map((line) => <li key={line}>Left out: {line}</li>)}</ul>}
        </div>
        <label className="ws-field">Subject<input value={subject} maxLength={200} onChange={(e) => { setSubject(e.target.value); setState({}); }} /></label>
        <label className="ws-field">Message<textarea rows={9} value={body} maxLength={20000} onChange={(e) => { setBody(e.target.value); setState({}); }} /></label>
        <p className="adm-hint">Write <code>{'{first_name}'}</code> where each person&apos;s first name should go. A footer is added in each
          person&apos;s language: who it was for, why they receive it{category === 'news' ? ', and how to unsubscribe' : ''}.</p>
        {category === 'official' && <label className="adm-check"><input type="checkbox" checked={officialOk} onChange={(e) => setOfficialOk(e.target.checked)} />
          This is about their account, plan, security or the terms, not promotion.</label>}
        <label className="adm-check"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!recipients || off} />
          I have read the email again: send it to {number(recipients)} {recipients === 1 ? 'person' : 'people'}.</label>
        <div className="adm-form-row">
          <button type="submit" className="ws-primary" disabled={!ready || state.busy}>{state.busy ? 'Starting…' : `Send to ${number(recipients)} ${recipients === 1 ? 'person' : 'people'}`}</button>
          {state.message && <p className="adm-feedback adm-error" role="alert">{state.message}</p>}
        </div>
      </form>}
    </aside>
  </div>;
}
